import { PropsOf, css } from '@emotion/react'
import styled from '@emotion/styled'
import { map, times } from 'lodash'
import {
	ChangeEventHandler,
	FC,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { HexColorPicker } from 'react-colorful'
import { useLocalStorage } from 'usehooks-ts'
import colors from '../colors'
import { decodeHexColor } from '../styles'
import { GlobalModalPicker, ModalPicker } from './modal'
import { Panel } from './panel'
import { CancelIcon } from '../icons'
import { CustomColorInput } from './input'
import { useToggle } from '../hooks'
import { Flex } from './layout'

const Square = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 32px;
	height: 32px;
	min-width: 32px;
	min-height: 32px;
	margin: 3px;
	border-radius: 4px;
	cursor: pointer;
	color: ${colors.heather};
	svg {
		width: 12px;
		height: 12px;
	}
`
const LIGHT_COLOR_THRESHOLD = 200 * 200
const ColorSquare: FC<
	{ color: string | null; selected: boolean } & Omit<
		PropsOf<typeof Square>,
		'color'
	>
> = ({ color, selected, ...rest }) => {
	const isLightColor = useMemo(() => {
		if (!color) return true
		const [r, g, b] = decodeHexColor(color)
		const hsp = 0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b)
		return hsp > LIGHT_COLOR_THRESHOLD
	}, [color])

	const borderColor = isLightColor ? colors.heather : color

	return (
		<Square
			{...rest}
			style={{
				backgroundColor: color || 'transparent',
				border: `2px solid ${selected ? colors.indigoBlue : borderColor}`,
				borderWidth: `${selected ? 2 : 1}px`,
			}}
		/>
	)
}

const CustomColorInputWrapper = styled.div`
	display: flex;
	border-radius: 4px;
	border: 2px solid #e8e8e8;
	overflow: hidden;
	height: 32px;
	margin: 3px;
	flex: 1;
	min-width: 6em;
`
const CustomColorInputMarker = styled.div`
	flex: 0;
	background-color: #e8e8e8;
	min-width: 28px;
	min-height: 28px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: #bbb;
`
const CustomColorInputStyled = styled.input`
	padding: 6px 10px;
	color: ${colors.black};
	border: none;
	min-width: 5em;
`

const STORE_LAST_COLORS = 10
const VALID_VALUE = /^[0-9a-fA-F]*$/
const DEFAULT_PRESETS: string[] = [
	'#bb56d6',
	'#fc6920',
	'#063d66',
	'#fdb92e',
	'#22d084',
	'#1b93e3',
	'#abb8c3',
	'#eb154c',
	'#f78ca7',
]
type Props = {
	presets?: string[]
	selected?: string | null
	showLast?: number
	onClose: () => unknown
	pointAndClick?: boolean
	storeLastUsed?: boolean
	className?: string
	style?: React.CSSProperties
	global?: boolean
	position?: 'left' | 'right'
	showRemove?: boolean
	filterColor?: (color: string) => boolean
	onChange: (color: string | null) => unknown
}
export const ColorPicker: FC<Props> & { defaultPresets: string[] } = ({
	presets = DEFAULT_PRESETS,
	selected,
	onClose,
	onChange,
	pointAndClick,
	showLast,
	className,
	style,
	global,
	position,
	showRemove,
	filterColor,
}) => {
	const [customColor, setCustomColor] = useState('')
	const [lastColors, updateLastColors] = useLocalStorage(
		'beelday-last-colors',
		[] as string[]
	)

	useEffect(() => {
		if (selected) {
			setCustomColor(selected.substring(1))
		}
	}, [selected])
	const isValid = customColor.length >= 6

	const handleCustomColorChange: ChangeEventHandler<HTMLInputElement> = e => {
		const { value } = e.target
		if (VALID_VALUE.test(value)) {
			setCustomColor(value)
		}
	}

	const handleCustomColor = () => {
		if (isValid) {
			const color = '#' + customColor

			if (filterColor && !filterColor(color)) return

			if (!lastColors.includes(color) && !presets.includes(color)) {
				const lastColorsCopy = [...lastColors]
				lastColorsCopy.push(color)
				if (lastColorsCopy.length > STORE_LAST_COLORS) {
					lastColorsCopy.splice(0, lastColorsCopy.length - STORE_LAST_COLORS)
				}
				updateLastColors(lastColorsCopy)
			}
			onChange(color)
			onClose()
		}
	}

	const ModalWrapper = global ? GlobalModalPicker : ModalPicker

	return (
		<ModalWrapper
			onClose={() => {
				if (customColor) {
					handleCustomColor()
				}
				onClose()
			}}
			className={className}
			style={style}
			position={position}
		>
			<Panel
				style={{
					padding: '15px',
					display: 'flex',
					flexWrap: 'wrap',
					width: '296px',
				}}
			>
				{showRemove ? (
					<ColorSquare
						key="remove-color"
						color={null}
						selected={false}
						onClick={() => {
							onChange(null)
							onClose()
						}}
					>
						<CancelIcon />
					</ColorSquare>
				) : null}
				{map(presets, color => (
					<ColorSquare
						key={color}
						color={color}
						selected={color === selected}
						onClick={() => {
							onChange(color)
							onClose()
						}}
					/>
				))}
				{times(showLast || 0, i => {
					const savedColor =
						lastColors[lastColors.length - ((showLast || 0) - i)]
					return (
						<ColorSquare
							key={`${i}${savedColor}`}
							color={savedColor || 'transparent'}
							selected={savedColor != null && savedColor === selected}
							onClick={() => {
								if (savedColor) {
									onChange(savedColor)
									onClose()
								}
							}}
						/>
					)
				})}
				<CustomColorInputWrapper>
					<CustomColorInputMarker>#</CustomColorInputMarker>
					<CustomColorInputStyled
						autoFocus={true}
						maxLength={6}
						minLength={6}
						value={customColor || ''}
						onChange={handleCustomColorChange}
						style={{ color: isValid ? colors.black : colors.red }}
						onKeyDown={e => {
							if (e.key === 'Enter') handleCustomColor()
						}}
					/>
				</CustomColorInputWrapper>
				{pointAndClick ? (
					<PointAndClickPicker
						color={null}
						// color={isValid ? customColor : selected}
						onChange={value => {
							if (filterColor && !filterColor(value)) return
							setCustomColor(value.replace(/^#/, ''))
							onChange(value)
						}}
					/>
				) : null}
			</Panel>
		</ModalWrapper>
	)
}
ColorPicker.defaultPresets = DEFAULT_PRESETS

const PointAndClickPicker = ({
	color,
	onChange,
}: {
	color?: string | null
	onChange: (color: string) => unknown
}) => (
	<div
		css={css`
			margin-top: 11px;
			width: 100%;
			.react-colorful {
				height: 200px;
				width: 100%;
			}
		`}
	>
		<HexColorPicker color={color || undefined} onChange={onChange} />
	</div>
)

// export const CustomColorInput = ({ onChange }: Partial<Props>): JSX.Element => {
// 	const [customColor, setCustomColor] = useState('')

// 	const isValid = customColor.length >= 6

// 	const handleCustomColorChange: ChangeEventHandler<HTMLInputElement> = e => {
// 		const { value } = e.target
// 		if (VALID_VALUE.test(value)) {
// 			setCustomColor(value)
// 		}
// 	}

// 	const handleCustomColor = () => {
// 		if (isValid) {
// 			const color = '#' + customColor
// 			onChange?.(color)
// 		}
// 	}
// 	return (
// 		<CustomColorInputWrapper>
// 			<CustomColorInputMarker>#</CustomColorInputMarker>
// 			<CustomColorInputStyled
// 				autoFocus={true}
// 				maxLength={6}
// 				minLength={6}
// 				value={customColor || ''}
// 				onChange={handleCustomColorChange}
// 				style={{ color: isValid ? colors.black : colors.red }}
// 				onKeyDown={e => {
// 					if (e.key === 'Enter') handleCustomColor()
// 				}}
// 			/>
// 		</CustomColorInputWrapper>
// 	)
// }

type ColorPickerProps = React.InputHTMLAttributes<HTMLInputElement> & {
	color: string
	onColorChange: (color: string | null) => void
}

const COLOR_PICKER_BOX_HEIGHT = 279
const COLOR_PICKER_INPUT_HEIGHT = 54

const ColorBox = styled.div`
	align-items: center;
	border-radius: 11px;
	border: 1px solid ${colors.borderLightGray};
	cursor: pointer;
	display: flex;
	height: 54px;
	justify-content: center;
	min-width: 54px;
`

export const ColorPickerInline = ({
	color,
	onColorChange,
	...rest
}: ColorPickerProps): JSX.Element | null => {
	const colorPicker = useToggle(false)

	const containerRef = useRef<HTMLDivElement>(null)

	const bcr = useRef<DOMRect | null>(null)

	const onClickColorBox = () => {
		if (containerRef.current) {
			bcr.current = containerRef.current.getBoundingClientRect()
		}
		colorPicker.setOn()
	}

	const calcPickerRender = () => {
		const documentScrollHeight = document.documentElement.scrollHeight
		if (
			documentScrollHeight <
			(bcr.current?.top || 0) +
				COLOR_PICKER_BOX_HEIGHT +
				COLOR_PICKER_INPUT_HEIGHT
		) {
			const x =
				documentScrollHeight -
				(bcr.current?.top || 0) -
				COLOR_PICKER_BOX_HEIGHT -
				COLOR_PICKER_INPUT_HEIGHT

			if (x < 0) {
				return (bcr.current?.top || 0) - COLOR_PICKER_BOX_HEIGHT - 4
			}
		} else {
			return (bcr.current?.top || 0) + COLOR_PICKER_INPUT_HEIGHT + 4
		}
	}

	return (
		<Flex style={{ position: 'relative' }} ref={containerRef}>
			{colorPicker.value && (
				<ColorPicker
					css={css`
						position: absolute;
						left: ${bcr.current?.left}px;
						top: ${calcPickerRender()}px;
					`}
					presets={[]}
					onChange={color => {
						onColorChange(color)
					}}
					onClose={() => {
						colorPicker.setOff()
					}}
					pointAndClick={true}
					showLast={4}
					selected={color}
					global={true}
				/>
			)}

			<ColorBox className="color-box" onClick={onClickColorBox}>
				<span
					css={css`
						all: unset;
						display: inline-block;
						border-radius: 50%;
						border: 1px solid ${colors.brightGray};
						width: 17px;
						height: 17px;
					`}
					style={{
						backgroundColor: color,
					}}
				/>
			</ColorBox>
			<CustomColorInput
				{...rest}
				className="color-input-wrapper"
				style={{
					height: '54px',
					borderRadius: '11px',
					maxWidth: '240px',
					width: '240px',
					marginLeft: '4px',
					flexGrow: 1,
					...rest.style,
				}}
				onChange={color => {
					onColorChange(color)
				}}
				value={color}
				hashWidth={54}
			/>
		</Flex>
	)
}

const COLOR_PICKER_BOX_HEIGHT_SMALL = 279
const COLOR_PICKER_INPUT_HEIGHT_SMALL = 32
const COLOR_PICKER_PANEL_WIDTH_SMALL = 296

const ColorBoxSmall = styled.div`
	align-items: center;
	border-radius: 8px;
	border: 1px solid ${colors.borderLightGray};
	cursor: pointer;
	display: flex;
	height: 32px;
	justify-content: center;
	min-width: 32px;
`

export const ColorPickerInlineSmall = ({
	color,
	onColorChange,
	...rest
}: ColorPickerProps): JSX.Element | null => {
	const colorPicker = useToggle(false)

	const containerRef = useRef<HTMLDivElement>(null)

	const bcr = useRef<DOMRect | null>(null)

	const onClickColorBox = () => {
		if (containerRef.current) {
			bcr.current = containerRef.current.getBoundingClientRect()
		}
		colorPicker.setOn()
	}

	const calcPickerRender = () => {
		const rect = bcr.current
		const viewportHeight = window.innerHeight
		const viewportWidth = window.innerWidth
		const margin = 8

		let top = (rect?.top || 0) + COLOR_PICKER_INPUT_HEIGHT_SMALL + 4

		const spaceBelow =
			viewportHeight - ((rect?.top || 0) + COLOR_PICKER_INPUT_HEIGHT_SMALL)
		if (spaceBelow < COLOR_PICKER_BOX_HEIGHT_SMALL) {
			top = (rect?.top || 0) - COLOR_PICKER_BOX_HEIGHT_SMALL - 4
			if (top < margin) top = margin
		}

		let left = rect?.left || 0
		if (left + COLOR_PICKER_PANEL_WIDTH_SMALL + margin > viewportWidth) {
			left = Math.max(
				margin,
				viewportWidth - COLOR_PICKER_PANEL_WIDTH_SMALL - margin
			)
		}
		if (left < margin) left = margin

		return { top, left }
	}

	return (
		<Flex style={{ position: 'relative', flex: 1 }} ref={containerRef}>
			{colorPicker.value && (
				<ColorPicker
					css={css`
						position: absolute;
						left: ${calcPickerRender().left}px;
						top: ${calcPickerRender().top}px;
					`}
					presets={[]}
					onChange={color => {
						onColorChange(color)
					}}
					onClose={() => {
						colorPicker.setOff()
					}}
					pointAndClick={true}
					showLast={4}
					selected={color}
					global={true}
				/>
			)}

			<ColorBoxSmall className="color-box" onClick={onClickColorBox}>
				<span
					css={css`
						all: unset;
						display: inline-block;
						border-radius: 50%;
						border: 1px solid ${colors.brightGray};
						width: 17px;
						height: 17px;
					`}
					style={{
						backgroundColor: color,
					}}
				/>
			</ColorBoxSmall>
			<CustomColorInput
				{...rest}
				className="color-input-wrapper"
				style={{
					height: '32px',
					borderRadius: '8px',
					maxWidth: '100%',
					width: '100%',
					marginLeft: '4px',
					flexGrow: 1,
					...rest.style,
				}}
				onChange={color => {
					onColorChange(color)
				}}
				value={color}
				hashWidth={32}
			/>
		</Flex>
	)
}
